import {
    Box,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    Avatar,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    Text,
    Icon,
    IconButton,
    useColorMode,
    useColorModeValue,
    useDisclosure,
    Tooltip,
    HStack,
} from '@chakra-ui/react';
import {
    MdSearch,
    MdNotifications,
    MdLogout,
    MdPerson,
    MdDarkMode,
    MdLightMode,
    MdMenu
} from 'react-icons/md';
import { useClerk, useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { GlobalChatDrawer } from '../GlobalChat/GlobalChatDrawer';
import { BanditIcon } from '../Common/BanditIcon';

interface HeaderProps {
    onOpen: () => void;
}

export function Header({ onOpen }: HeaderProps) {
    const { user } = useUser();
    const { signOut } = useClerk();
    const { colorMode, toggleColorMode } = useColorMode();
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState('');
    const { isOpen: isAgentOpen, onOpen: onAgentOpen, onClose: onAgentClose } = useDisclosure();

    const bgVal = useColorModeValue('white', 'navy.800');
    const borderVal = useColorModeValue('gray.200', 'navy.600');
    const inputBg = useColorModeValue('gray.50', 'navy.700');

    const handleSearch = () => {
        const q = searchValue.trim();
        if (q.length >= 2) {
            navigate(`/search?q=${encodeURIComponent(q)}`);
            setSearchValue('');
        }
    };

    return (
        <Flex
            as="header"
            h="80px"
            bg={bgVal}
            borderBottom="1px solid"
            borderColor={borderVal}
            px={{ base: 4, md: 6 }}
            align="center"
            justify="space-between"
            position="sticky"
            top="0"
            zIndex="sticky"
            boxShadow="sm"
        >
            <HStack spacing={4} flex={1}>
                {/* Mobile Menu Toggle */}
                <IconButton
                    display={{ base: 'flex', md: 'none' }}
                    onClick={onOpen}
                    variant="outline"
                    aria-label="open menu"
                    icon={<MdMenu />}
                />

                {/* Search - Shrink on mobile */}
                <InputGroup maxW={{ base: '200px', md: '400px' }}>
                    <InputLeftElement pointerEvents="none">
                        <Icon as={MdSearch} color="gray.500" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search..."
                        bg={inputBg}
                        border="1px solid"
                        borderColor={borderVal}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                        _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                        fontSize="sm"
                    />
                </InputGroup>
            </HStack>

            {/* Right side */}
            <Flex align="center" gap={{ base: 2, md: 4 }}>
                {/* Theme Toggle - Hide icon label on mobile */}
                <Tooltip label={colorMode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
                    <IconButton
                        aria-label="Toggle Color Mode"
                        icon={colorMode === 'light' ? <MdDarkMode /> : <MdLightMode />}
                        onClick={toggleColorMode}
                        variant="ghost"
                        fontSize="xl"
                    />
                </Tooltip>

                {/* AI Agent */}
                <Tooltip label="BidBandit AI Agent">
                    <IconButton
                        aria-label="AI Agent"
                        icon={<BanditIcon boxSize={6} variant="red" />}
                        onClick={onAgentOpen}
                        variant="ghost"
                        fontSize="xl"
                        color="brand.500"
                    />
                </Tooltip>

                {/* Notifications - Hidden on very small screens if needed, keeping for now */}
                <IconButton
                    display={{ base: 'none', sm: 'flex' }}
                    aria-label="Notifications"
                    icon={<MdNotifications />}
                    variant="ghost"
                    fontSize="xl"
                />

                {/* User Menu */}
                <Menu>
                    <MenuButton>
                        <Flex align="center" gap={3} cursor="pointer">
                            <Avatar
                                size="sm"
                                name={user?.fullName || 'User'}
                                src={user?.imageUrl}
                            />
                            <Box display={{ base: 'none', lg: 'block' }}>
                                <Text fontSize="sm" fontWeight="medium">
                                    {user?.fullName || 'User'}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    {user?.publicMetadata?.role as string || 'Viewer'}
                                </Text>
                            </Box>
                        </Flex>
                    </MenuButton>
                    <MenuList>
                        <MenuItem icon={<MdPerson />}>Profile</MenuItem>
                        <MenuDivider />
                        <MenuItem icon={<MdLogout />} onClick={() => signOut()}>
                            Sign Out
                        </MenuItem>
                    </MenuList>
                </Menu>
            </Flex>

            <GlobalChatDrawer isOpen={isAgentOpen} onClose={onAgentClose} />
        </Flex>
    );
}
